<template>
  <transition name="modal" id="product-list-dialog" v-if="visible">
    <DxPopup
      class="vertical-center"
      ref="productSelector"
      id="dx-dialog"
      width="100vw"
      height="100vh"
      :title="$t('common.products')"
      :hide-on-outside-click="false"
      :drag-enabled="false"
      :show-close-button="true"
      :visible="visible"
      :resize-enabled="true"
      @hidden="onClose"
    >
      <div style="display: flex; flex-direction: column; height: 95%">
        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px">
          <label style="display: flex; align-items: center; gap: 8px; cursor: pointer">
            <input
              type="radio"
              v-model.number="product_type"
              :value="1"
              @change="$nextTick(() => $refs.fitoFerProductGrid && $refs.fitoFerProductGrid.instance.refresh())"
            />
            <span>{{ $t("products_catalog.fito_products.title") || "Fito" }}</span>
          </label>

          <label style="display: flex; align-items: center; gap: 8px; cursor: pointer">
            <input
              type="radio"
              v-model.number="product_type"
              :value="2"
              @change="$nextTick(() => $refs.fitoFerProductGrid && $refs.fitoFerProductGrid.instance.refresh())"
            />
            <span>{{ $t("products_catalog.fertility_products.title") || "Fertility" }}</span>
          </label>
        </div>
        <div class="row" v-if="emptyAfterSearch">
          <button v-if="emptyAfterSearch" class="btn btn-outline-secondary col-lg-2 add-suggestion-bttn" @click="addProductSuggestion">
            {{ $t("products_catalog.create_suggestion") }}
          </button>
        </div>
        <DxDataGrid
          ref="fitoFerProductGrid"
          class="product_grid"
          :data-source="productsDataSurce"
          :show-borders="true"
          :column-auto-width="true"
          :remote-operations="true"
          :hover-state-enabled="true"
          :repaint-changes-only="true"
          :show-column-headers="false"
          :selected-row-keys="selectedProductUuids"
          @selection-changed="onGridSelectionChanged($event)"
          @row-click="onRowClick"
          :paging="{ pageSize: 25 }"
          :height="'88%'"
          :scrolling="{ mode: 'virtual' }"
        >
          <DxColumn dataField="name" :caption="$t('common.name')" :filter-operations="['contains']" :allowSorting="false" width="70%" />
          <DxColumn
            dataField="state.name"
            :caption="$t('common.status')"
            :filter-operations="['contains']"
            :allowSorting="false"
            width="30%"
          />
          <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
          <DxFilterRow :visible="true" />
        </DxDataGrid>
      </div>
      <AddProductSuggestion
        ref="addProductSuggestion"
        v-if="showAddProductSuggestionDialog"
        :visible="showAddProductSuggestionDialog"
        @close="onSuggestionClose"
        :data="suggestionData"
        :mobile="true"
      />
    </DxPopup>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxLookup, DxPager } from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";
import AddProductSuggestion from "@/components/AddProductSuggestion.vue";
import { getDxFilterValueByKey } from "@/helpers/dxGridHelper";

export default {
  name: "FitoFertilityProductSelectorMobile",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxPaging,
    DxLookup,
    DxPager,
    AddProductSuggestion,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedProductUuids: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      productsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
            with_custom_products: false
          };

          if (this.filterData && Object.keys(this.filterData).length > 0) {
            Object.assign(payload, this.filterData);
          }

          try {
            let response = null;
            if (this.product_type === 1) {
              response = await this.getFitoProducts(payload);
            } else if (this.product_type === 2) {
              response = await this.getFertilityProducts(payload);
            }

            dataSource.data = response?.data || [];
            dataSource.totalCount = response?.pagination?.total ?? dataSource.data.length;

            // Side-effect free: just compute and defer UI state update
            const filterActive = Boolean(loadOptions.searchValue || (loadOptions.filter && loadOptions.filter.length));
            const nameFilter = getDxFilterValueByKey("name", loadOptions.filter);

            this._queueNoDataStateUpdate({
              total: dataSource.totalCount,
              filterActive,
              nameFilter,
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
          }
        },
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      localSelectedProductUuids: [...this.selectedProductUuids],
      product_type: 1, // 1 - Fito, 2 - Fertility
      // new reactive flags
      emptyAfterSearch: false,
      suggestionData: {},
      showAddProductSuggestionDialog: false,
      // guards
      _suppressNoDataUpdates: false,
      _noDataUpdateScheduled: false,
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
  },
  watch: {
    selectedProductUuids: {
      deep: true,
      handler(newData) {
        console.log("selectedProductUuids changed:", newData);

        this.localSelectedProductUuids = [...newData];
        this.$refs.fitoFerProductGrid.instance.selectRows(newData, false);
      },
    },
  },
  methods: {
    ...mapActions({
      getFitoProducts: "fitoProduct/getFitoProducts",
      getFertilityProducts: "fertilityProduct/getFertilityProducts",
    }),
    onClose() {
      this.$emit("close");
    },
    onContinue() {
      const selectedRows = this.$refs.fitoFerProductGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected products to the parent component

      this.$refs.fitoFerProductGrid.instance.clearSelection();
      this.localSelectedProductUuids = [];
      this.$emit("update:selectedProductUuids", []);
    },
    onGridSelectionChanged(e) {},
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        this.onContinue(); // Emit selection immediately on row click
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedProductUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedProductUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedProductUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.fitoFerProductGrid.instance.selectRows(this.selectedProductUuids, false);
    },
    // Defer and guard UI updates that could trigger grid refresh
    _queueNoDataStateUpdate({ total, filterActive, nameFilter }) {
      if (this._suppressNoDataUpdates) return;
      if (this._noDataUpdateScheduled) return;

      this._noDataUpdateScheduled = true;
      this.$nextTick(() => {
        this._noDataUpdateScheduled = false;
        if (this._suppressNoDataUpdates) return;

        const shouldShow = total === 0 && filterActive;
        // Only update when value actually changes
        if (this.emptyAfterSearch !== shouldShow) {
          this.emptyAfterSearch = shouldShow;
        }
        if (shouldShow) {
          const nextSuggestion = {
            sm_product_uuid: this.productUuid,
            source_type: this.product_type === 1 ? "FITO" : "FERTILITY",
            name: nameFilter || "",
          };
          // Avoid replacing object if same content
          const same =
            this.suggestionData &&
            this.suggestionData.name === nextSuggestion.name &&
            this.suggestionData.source_type === nextSuggestion.source_type &&
            this.suggestionData.sm_product_uuid === nextSuggestion.sm_product_uuid;

          if (!same) {
            this.suggestionData = nextSuggestion;
          }
        }
      });
    },
    onSuggestionClose() {
      this.showAddProductSuggestionDialog = false;
      // Unfreeze after modal closes
      this.$nextTick(() => {
        this._suppressNoDataUpdates = false;
      });
    },
    addProductSuggestion() {
      // Freeze no-data updates while popup is open to prevent reload loop
      this._suppressNoDataUpdates = true;
      this.showAddProductSuggestionDialog = true;
    },
  },
};
</script>
<style scoped>
.add-suggestion-bttn {
  margin-bottom: 12px;
  margin-left: 10px;
  width: 90%;
}
</style>
